// Formulário de criar/editar contato — RHF + zodResolver, máscaras de telefone/whatsapp.
// Reutiliza primitivos do domínio Client (Field, Input, Select, Textarea, PillButton).

import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createClientContactSchema,
  type ContactRoleSchema,
  type CreateClientContactDto,
  type CreateClientContactInput,
} from '@control-center/shared';
import { useEffect } from 'react';

import { toast } from 'sonner';

import { firstErrorMessage } from '../../lib/form-errors';
import { maskPhone } from '../../lib/masks';
import { Field, Input, Select, Textarea } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';

const ROLE_OPTIONS: { value: ContactRoleSchema; label: string }[] = [
  { value: 'dono', label: 'Dono' },
  { value: 'socio', label: 'Sócio' },
  { value: 'gerente', label: 'Gerente' },
  { value: 'atendente', label: 'Atendente' },
  { value: 'financeiro', label: 'Financeiro' },
  { value: 'fiscal', label: 'Fiscal' },
  { value: 'comercial', label: 'Comercial' },
  { value: 'outro', label: 'Outro' },
];

type FormValues = CreateClientContactInput;

export interface ContactFormProps {
  /** Quando passado, modo de edição: preenche campos. */
  defaultValues?: Partial<FormValues>;
  submitLabel?: string;
  isPending?: boolean;
  onSubmit: (values: CreateClientContactDto) => void | Promise<void>;
  onCancel?: () => void;
}

const EMPTY_DEFAULTS: FormValues = {
  name: '',
  role: 'financeiro',
  email: '',
  phone: '',
  whatsapp: '',
  notes: '',
  is_active: true,
  preferred_channel: 'whatsapp',
  receives_billing_notifications: false,
  receives_fiscal_documents: false,
  inactivated_reason: null,
};

export function ContactForm({
  defaultValues,
  submitLabel = 'Salvar contato',
  isPending,
  onSubmit,
  onCancel,
}: ContactFormProps): JSX.Element {
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(createClientContactSchema),
    defaultValues: { ...EMPTY_DEFAULTS, ...defaultValues },
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (defaultValues) {
      reset({ ...EMPTY_DEFAULTS, ...defaultValues });
    }
  }, [defaultValues, reset]);

  function submit(values: FormValues): void | Promise<void> {
    const parsed = createClientContactSchema.parse(values);
    return onSubmit(parsed);
  }

  // Nota Quinn re-check F5: ZodIssue de "pelo menos um canal" cai em path ['email'].
  // Mostramos a mensagem específica logo abaixo do campo de email pra contexto.
  const channelError = errors.email?.message;

  return (
    <form
      onSubmit={(e) =>
        void handleSubmit(submit, (errs) =>
          toast.error(firstErrorMessage(errs) ?? 'Formulário tem campos inválidos.'),
        )(e)
      }
      className="space-y-5"
      noValidate
    >
      <Field label="Nome" required error={errors.name?.message}>
        {({ inputId, describedBy }) => (
          <Input
            id={inputId}
            type="text"
            autoComplete="name"
            placeholder="Maria Pereira"
            hasError={Boolean(errors.name)}
            aria-describedby={describedBy}
            {...register('name')}
          />
        )}
      </Field>

      <Field label="Função no cliente" required error={errors.role?.message}>
        {({ inputId, describedBy }) => (
          <Select
            id={inputId}
            hasError={Boolean(errors.role)}
            aria-describedby={describedBy}
            {...register('role')}
          >
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field
        label="Email"
        hint="Pelo menos um canal: email, telefone ou WhatsApp."
        error={channelError}
      >
        {({ inputId, describedBy }) => (
          <Input
            id={inputId}
            type="email"
            autoComplete="email"
            placeholder="contato@empresa.com"
            hasError={Boolean(errors.email)}
            aria-describedby={describedBy}
            {...register('email')}
          />
        )}
      </Field>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Field label="Telefone" error={errors.phone?.message}>
          {({ inputId, describedBy }) => (
            <Controller
              control={control}
              name="phone"
              render={({ field }) => (
                <Input
                  id={inputId}
                  type="tel"
                  autoComplete="tel"
                  placeholder="(11) 9 9999-9999"
                  hasError={Boolean(errors.phone)}
                  aria-describedby={describedBy}
                  value={maskPhone(field.value ?? '')}
                  onChange={(e) => field.onChange(maskPhone(e.target.value))}
                  onBlur={field.onBlur}
                />
              )}
            />
          )}
        </Field>

        <Field label="WhatsApp" error={errors.whatsapp?.message}>
          {({ inputId, describedBy }) => (
            <Controller
              control={control}
              name="whatsapp"
              render={({ field }) => (
                <Input
                  id={inputId}
                  type="tel"
                  autoComplete="tel"
                  placeholder="(11) 9 9999-9999"
                  hasError={Boolean(errors.whatsapp)}
                  aria-describedby={describedBy}
                  value={maskPhone(field.value ?? '')}
                  onChange={(e) => field.onChange(maskPhone(e.target.value))}
                  onBlur={field.onBlur}
                />
              )}
            />
          )}
        </Field>
      </div>

      <Field label="Canal preferido" error={errors.preferred_channel?.message}>
        {({ inputId, describedBy }) => (
          <Select
            id={inputId}
            hasError={Boolean(errors.preferred_channel)}
            aria-describedby={describedBy}
            {...register('preferred_channel')}
          >
            <option value="whatsapp">WhatsApp</option>
            <option value="email">Email</option>
            <option value="ambos">Ambos</option>
          </Select>
        )}
      </Field>

      <fieldset className="space-y-2">
        <legend className="text-[12px] font-medium text-ink-2">Recebe notificações</legend>
        <label className="flex items-center gap-2 text-[13px] text-ink-2">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-line bg-panel-2 text-accent focus:ring-0"
            {...register('receives_billing_notifications')}
          />
          Cobrança e inadimplência
        </label>
        <label className="flex items-center gap-2 text-[13px] text-ink-2">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-line bg-panel-2 text-accent focus:ring-0"
            {...register('receives_fiscal_documents')}
          />
          Documentos fiscais (NF)
        </label>
      </fieldset>

      <Field
        label="Observações"
        hint="Opcional. Até 2000 caracteres."
        error={errors.notes?.message}
      >
        {({ inputId, describedBy }) => (
          <Textarea
            id={inputId}
            rows={3}
            maxLength={2000}
            placeholder="Notas internas sobre o contato"
            hasError={Boolean(errors.notes)}
            aria-describedby={describedBy}
            {...register('notes')}
          />
        )}
      </Field>

      <div className="flex items-center justify-end gap-2 pt-2">
        {onCancel && (
          <PillButton type="button" onClick={onCancel} disabled={isPending}>
            Cancelar
          </PillButton>
        )}
        <PillButton type="submit" solid disabled={isPending}>
          {isPending ? 'Salvando…' : submitLabel}
        </PillButton>
      </div>
    </form>
  );
}
